import { ComponentRef, Injectable, signal, Type, ViewContainerRef } from '@angular/core';
import { Subject } from 'rxjs';

import { ChipVariants } from '../chip/chip.component';
import { SidepanelDefaultFooterComponent } from './sidepanel-default-footer.component';

type ComponentInputs = Record<string, unknown>;

@Injectable({
  providedIn: 'root',
})
export class SidepanelService {
  isOpen = signal(false);

  title = signal<string | undefined>(undefined);
  chips = signal<{ label: string; variant: ChipVariants['variant'] }[]>([]);
  description = signal<string | undefined>(undefined);

  loading = signal<boolean>(false);
  hasPreviousSidepanel = signal<boolean>(false);

  submit = new Subject();

  #componentToDisplay?: ComponentToDisplay;
  #previousComponentToDisplay?: ComponentToDisplay;
  #currentComponentRef?: ComponentRef<any>;
  #footerComponentRef?: ComponentRef<any>;
  #currentViewContainerRef?: ViewContainerRef;
  #footerViewContainerRef?: ViewContainerRef;

  open() {
    this.resetComponentToDisplay();
    this.buildSidepanelComponent();
    this.isOpen.set(true);
  }

  setAdditionalComponentToDisplay(
    title: string,
    component: Type<any>,
    description?: string,
    inputs?: ComponentInputs,
    footer?: Type<any>,
  ) {
    this.#previousComponentToDisplay = this.#componentToDisplay;
    this.hasPreviousSidepanel.set(true);
    this.setComponentToDisplay(title, component, description, inputs, footer);
  }

  close() {
    if (this.#previousComponentToDisplay) {
      this.resetComponentToDisplay();
      this.setComponentToDisplay(
        this.#previousComponentToDisplay.title,
        this.#previousComponentToDisplay.component,
        this.#previousComponentToDisplay.description,
        this.#previousComponentToDisplay.inputs,
        this.#previousComponentToDisplay.footer,
      );
      this.#previousComponentToDisplay = undefined;
      this.hasPreviousSidepanel.set(false);
      this.open();
    } else {
      this.isOpen.set(false);
      this.resetComponentToDisplay();
    }
  }

  setComponentToDisplay(
    title: string,
    component: Type<any>,
    description?: string,
    inputs?: ComponentInputs,
    footer?: Type<any>,
    chips?: { label: string; variant: ChipVariants['variant'] }[],
  ) {
    this.#componentToDisplay = { component, inputs: inputs ?? {}, title, description, footer, chips };
    this.title.set(title);
    this.description.set(description);
    this.chips.set(chips ?? []);
  }

  resetComponentToDisplay() {
    this.#currentComponentRef?.destroy();
    this.#currentComponentRef = undefined;
    this.#footerComponentRef?.destroy();
    this.#footerComponentRef = undefined;
  }

  updateComponentInputs(inputs: ComponentInputs) {
    if (!this.#currentComponentRef) return;

    Object.keys(inputs).forEach((key) => this.#currentComponentRef!.setInput(key, inputs[key]));
  }

  setViewContainerRef(vcr: ViewContainerRef) {
    this.#currentViewContainerRef = vcr;
  }

  setFooterViewContainerRef(vcr: ViewContainerRef) {
    this.#footerViewContainerRef = vcr;
  }

  buildSidepanelComponent() {
    if (!this.#componentToDisplay) return;

    this.#currentViewContainerRef?.clear();
    this.#currentComponentRef = this.#currentViewContainerRef?.createComponent(this.#componentToDisplay.component);
    this.#footerComponentRef = this.#footerViewContainerRef?.createComponent(
      this.#componentToDisplay.footer ? this.#componentToDisplay.footer : SidepanelDefaultFooterComponent,
    );
    this.updateComponentInputs(this.#componentToDisplay.inputs ?? {});
  }

  onSubmit() {
    this.submit.next(true);
  }
}

type ComponentToDisplay = {
  title: string;
  component: Type<any>;
  description?: string;
  inputs?: ComponentInputs;
  footer?: Type<any>;
  chips?: { label: string; variant: ChipVariants['variant'] }[];
};
